import type {
  CoachingGoal,
  CoachingGoalMilestone,
  CoachingGoalReflection,
  CoachingGoalStatus,
} from '@efdigital/ll-addon-data';
import GoalCreateForm from '@efdigital/ll-xceed-v2/components/coaching/goal-create-form.gts';
import GoalDeleteModal from '@efdigital/ll-xceed-v2/components/coaching/goal-delete-modal.gts';
import GoalProgressRing from '@efdigital/ll-xceed-v2/components/coaching/goal-progress-ring.gts';
import GoalReflections from '@efdigital/ll-xceed-v2/components/coaching/goal-reflections.gts';
import { updateCoachingGoalMilestone } from '@efdigital/ll-xceed-v2/data/builders/coaching/coaching-goal-milestones';
import {
  deleteCoachingGoal,
  updateCoachingGoal,
} from '@efdigital/ll-xceed-v2/data/builders/coaching/coaching-goals';
import { milestonesForGoal } from '@efdigital/ll-xceed-v2/utils/coaching/goal-children';
import {
  formatGoalTargetDate,
  goalCardProgress,
} from '@efdigital/ll-xceed-v2/utils/coaching/goal-progress';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Store } from '@warp-drive/core';

interface GoalDetailSignature {
  Args: {
    goal: CoachingGoal;
    milestones: readonly CoachingGoalMilestone[];
    reflections: readonly CoachingGoalReflection[];
    backRoute: string;
    backModels: unknown[];
    detailRoute: string;
    detailModels: unknown[];
    refreshRoute: string;
    role: 'coach' | 'coachee';
    authorship?: string | null;
    startInEdit?: boolean;
  };
  Element: HTMLDivElement;
}

const STATUS_LABEL: Record<CoachingGoalStatus, string> = {
  active: 'Active',
  achieved: 'Achieved',
  dropped: 'Dropped',
};

export default class GoalDetail extends Component<GoalDetailSignature> {
  @service declare router: RouterService;
  @service declare store: Store;

  @tracked editing = Boolean(this.args.startInEdit);
  @tracked deleting = false;
  @tracked completing = false;
  @tracked errorMessage: string | null = null;

  get milestones(): CoachingGoalMilestone[] {
    return milestonesForGoal(this.args.milestones, this.args.goal.id!);
  }

  get progress() {
    return goalCardProgress(this.args.goal, this.milestones);
  }

  get targetDateLabel(): string | null {
    return formatGoalTargetDate(this.args.goal.targetDate);
  }

  get status(): CoachingGoalStatus {
    return this.args.goal.status ?? 'active';
  }

  get isAchieved(): boolean {
    return this.status === 'achieved';
  }

  get statusLabel(): string {
    return STATUS_LABEL[this.status] ?? this.status;
  }

  get statusBadgeClass(): string {
    if (this.status === 'achieved') {
      return 'badge rounded-pill text-uppercase text-bg-success';
    }

    if (this.status === 'dropped') {
      return 'badge rounded-pill text-uppercase text-bg-secondary';
    }

    return 'badge rounded-pill text-uppercase text-bg-primary';
  }

  get statusIcon(): string {
    if (this.status === 'achieved') {
      return 'ph:check-circle-fill';
    }

    if (this.status === 'dropped') {
      return 'ph:minus-circle';
    }

    return 'ph:clock';
  }

  toggleMilestone = async (milestone: CoachingGoalMilestone) => {
    const previous = milestone.completed;
    this.errorMessage = null;
    milestone.completed = !previous;

    try {
      await this.store.request(
        updateCoachingGoalMilestone(milestone, {
          completed: milestone.completed,
        })
      );
    } catch (error) {
      milestone.rollbackAttributes();
      milestone.completed = previous;
      console.error(error);
      this.errorMessage =
        'The milestone could not be updated. Please try again.';
      await this.reconcileGoals();
    }
  };

  markComplete = async () => {
    if (this.completing || this.isAchieved) {
      return;
    }

    this.completing = true;
    this.errorMessage = null;
    const previous = this.args.goal.status;

    try {
      this.args.goal.status = 'achieved';
      await this.store.request(updateCoachingGoal(this.args.goal));
      await this.refreshGoals();
    } catch (error) {
      console.error(error);
      this.args.goal.rollbackAttributes();
      this.args.goal.status = previous;
      this.errorMessage =
        'The goal could not be completed. The latest server state is being restored.';
      await this.reconcileGoals();
    } finally {
      this.completing = false;
    }
  };

  revertComplete = async () => {
    if (this.completing || !this.isAchieved) {
      return;
    }

    this.completing = true;
    this.errorMessage = null;
    const previous = this.args.goal.status;

    try {
      this.args.goal.status = 'active';
      await this.store.request(updateCoachingGoal(this.args.goal));
      await this.refreshGoals();
    } catch (error) {
      console.error(error);
      this.args.goal.rollbackAttributes();
      this.args.goal.status = previous;
      this.errorMessage =
        'The goal could not be made active. The latest server state is being restored.';
      await this.reconcileGoals();
    } finally {
      this.completing = false;
    }
  };

  toggleGoalCompletion = async (event: Event) => {
    const completed = (event.target as HTMLInputElement).checked;

    if (completed) {
      await this.markComplete();
      return;
    }

    await this.revertComplete();
  };

  startEditing = () => {
    this.editing = true;
  };

  refreshGoals = async () => {
    await this.router.refresh(this.args.refreshRoute);
  };

  reconcileGoals = async () => {
    try {
      await this.refreshGoals();
    } catch (error) {
      console.error(error);
    }
  };

  stopEditing = () => {
    this.editing = false;

    if (!this.args.startInEdit || !this.args.goal.id) {
      return;
    }

    void this.router.replaceWith(
      this.args.detailRoute,
      ...this.args.detailModels,
      { queryParams: { edit: null } }
    );
  };

  afterDelete = () => {
    void this.router.transitionTo(this.args.backRoute, ...this.args.backModels);
  };

  deleteGoal = async () => {
    if (this.deleting) {
      return;
    }

    this.deleting = true;
    this.errorMessage = null;

    try {
      await this.store.request(deleteCoachingGoal(this.args.goal));
    } catch (error) {
      console.error(error);
      this.errorMessage = 'The goal could not be deleted. Please try again.';
      this.deleting = false;
      return;
    }

    try {
      await this.refreshGoals();
      this.afterDelete();
    } catch (error) {
      console.error(error);
      this.errorMessage =
        'The goal was deleted, but the goals list could not be refreshed.';
      this.deleting = false;
    }
  };

  <template>
    <div data-test-goal-detail ...attributes>
      <LinkTo
        @route={{@backRoute}}
        @models={{@backModels}}
        class="btn btn-link link-primary text-decoration-none d-inline-flex align-items-center gap-1 px-0 mb-3"
        data-test-goal-back
      >
        <iconify-icon inline icon="ph:caret-left" aria-hidden="true" />
        Back to goals
      </LinkTo>

      {{#if this.editing}}
        <GoalCreateForm
          @goal={{@goal}}
          @milestones={{@milestones}}
          @onCreated={{this.stopEditing}}
          @onClose={{this.stopEditing}}
          @onDeleted={{this.afterDelete}}
        />
      {{else}}
        <div class="d-flex flex-column gap-3">
          {{#if this.errorMessage}}
            <p class="alert alert-danger mb-0" role="alert">
              {{this.errorMessage}}
            </p>
          {{/if}}
          <div class="card border shadow-sm rounded-4 overflow-hidden">
            <div class="card-body p-4">
              <div
                class="d-flex flex-column flex-sm-row justify-content-between align-items-stretch align-items-sm-start gap-3 mb-4"
              >
                <div class="min-w-0">
                  <span
                    class="{{this.statusBadgeClass}}
                      d-inline-flex align-items-center gap-1"
                  >
                    <iconify-icon
                      inline
                      icon={{this.statusIcon}}
                      aria-hidden="true"
                    />
                    {{this.statusLabel}}
                  </span>
                  <h1 class="h4 fw-bold mb-0 mt-2">{{@goal.title}}</h1>
                </div>
                <div
                  class="align-self-stretch align-self-sm-start border rounded-3 px-3 py-2"
                >
                  <div class="form-check mb-0">
                    <input
                      id="goal-completed"
                      class="form-check-input"
                      type="checkbox"
                      checked={{this.isAchieved}}
                      disabled={{this.completing}}
                      data-test-goal-complete
                      {{on "change" this.toggleGoalCompletion}}
                    />
                    <label
                      class="form-check-label fw-semibold"
                      for="goal-completed"
                    >
                      Goal completed
                    </label>
                  </div>
                  <p class="small text-body-secondary mb-0 mt-1">
                    {{if
                      this.completing
                      "Saving…"
                      "Check this when the overall goal has been achieved."
                    }}
                  </p>
                </div>
              </div>

              <div
                class="d-flex flex-column flex-md-row align-items-start gap-3 gap-md-4 mb-4"
              >
                <GoalProgressRing
                  @percent={{this.progress.percent}}
                  @size={{96}}
                  @strokeWidth={{8}}
                />

                <div class="flex-grow-1 min-w-0">
                  {{#if @goal.detail}}
                    <p class="mb-3">{{@goal.detail}}</p>
                  {{/if}}
                  {{#if this.targetDateLabel}}
                    <p
                      class="d-inline-flex align-items-center gap-2 small text-body-secondary mb-0"
                    >
                      <iconify-icon inline icon="ph:flag" aria-hidden="true" />
                      Target:
                      {{this.targetDateLabel}}
                    </p>
                  {{/if}}
                  {{#if @authorship}}
                    <p
                      class="small text-body-secondary mb-0 mt-2"
                    >{{@authorship}}</p>
                  {{/if}}
                </div>
              </div>

              {{#if this.milestones.length}}
                <p class="gud-eyebrow text-body-secondary mb-3">
                  Milestones ·
                  {{this.progress.completedMilestones}}/{{this.progress.totalMilestones}}
                </p>

                {{#each this.milestones key="id" as |milestone|}}
                  <button
                    type="button"
                    class="w-100 border d-flex align-items-center gap-3 p-3 rounded-3 mb-2 text-start
                      {{if
                        milestone.completed
                        'border-success border-2'
                        'bg-body border-primary'
                      }}"
                    aria-pressed={{if milestone.completed "true" "false"}}
                    data-test-goal-milestone
                    {{on "click" (fn this.toggleMilestone milestone)}}
                  >
                    <span
                      class="ll-avatar-xs rounded border d-inline-flex align-items-center justify-content-center flex-shrink-0
                        {{if
                          milestone.completed
                          'bg-success border-success text-white'
                          'bg-body border-primary'
                        }}"
                      aria-hidden="true"
                    >
                      {{#if milestone.completed}}
                        <iconify-icon inline icon="ph:check-bold" />
                      {{/if}}
                    </span>
                    <span
                      class="flex-grow-1
                        {{if
                          milestone.completed
                          'text-decoration-line-through'
                        }}"
                    >{{milestone.title}}</span>
                  </button>
                {{/each}}
              {{else}}
                <p class="gud-eyebrow text-body-secondary mb-2">Milestones</p>
                <p class="text-body-secondary mb-0">No milestones yet for this
                  goal. Edit the goal to add some.</p>
              {{/if}}
            </div>

            <div
              class="card-footer bg-transparent border-top d-flex flex-column flex-sm-row justify-content-between gap-2 py-3 px-4"
            >
              <GoalDeleteModal
                @deleting={{this.deleting}}
                @onConfirm={{this.deleteGoal}}
                as |showDeleteModal|
              >
                <button
                  type="button"
                  class="btn btn-outline-primary rounded-pill px-4"
                  disabled={{this.deleting}}
                  data-test-goal-delete
                  {{on "click" showDeleteModal}}
                >
                  <iconify-icon inline icon="ph:trash" aria-hidden="true" />
                  Delete
                </button>
              </GoalDeleteModal>
              <div class="d-grid d-sm-flex flex-sm-wrap gap-2 ms-sm-auto">
                <button
                  type="button"
                  class="btn btn-outline-primary rounded-pill px-4"
                  {{on "click" this.startEditing}}
                >
                  Edit
                </button>
                <LinkTo
                  @route={{@backRoute}}
                  @models={{@backModels}}
                  class="btn btn-outline-primary rounded-pill px-4"
                >
                  Done
                </LinkTo>
              </div>
            </div>
          </div>

          {{#if @goal.id}}
            <GoalReflections
              @goal={{@goal}}
              @reflections={{@reflections}}
              @role={{@role}}
              @onSaved={{this.refreshGoals}}
            />
          {{/if}}
        </div>
      {{/if}}
    </div>
  </template>
}
